<script setup lang="ts">
// Shared line-icon set used by the sidebar / icon-rail navigation.
withDefaults(defineProps<{ icon: string; size?: number }>(), { size: 16 })
</script>

<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    style="flex-shrink: 0"
  >
    <template v-if="icon === 'home'">
      <path d="M3 10l9-7 9 7" />
      <path d="M5 9v11h14V9" />
    </template>
    <template v-else-if="icon === 'edit'">
      <path d="M4 20h4L20 8l-4-4L4 16v4z" />
      <path d="M14 6l4 4" />
    </template>
    <template v-else-if="icon === 'rsvp'">
      <path d="M4 6h16v12H4z" />
      <path d="M4 6l8 7 8-7" />
    </template>
    <template v-else-if="icon === 'guests'">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M15 14c3 0 5 1.7 5 4" />
    </template>
    <template v-else-if="icon === 'templates'">
      <path d="M4 5h16v14H4z" />
      <path d="M4 9h16M9 9v10" />
    </template>
    <template v-else-if="icon === 'globe'">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </template>
    <template v-else-if="icon === 'settings'">
      <circle cx="12" cy="12" r="3" />
      <path
        d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"
      />
    </template>
    <!-- Očko — the assistant mark drawn in this set's grammar (see AiMark.vue
         for the brand-weight drawing). Pupil is filled, so it needs its own
         fill/stroke; the ring is a touch heavier than the set default so the
         pupil still has room at 16 px. -->
    <template v-else-if="icon === 'assistant'">
      <circle cx="9.5" cy="8.5" r="4.5" stroke-width="1.7" />
      <circle cx="9.5" cy="8.5" r="1.6" fill="currentColor" stroke="none" />
      <path d="M16 3.6V20.4" stroke-width="1.7" />
    </template>
    <!-- Marks a targeted section (a block with text lines; crop marks lost
         their shape at the 13-14 px the chips use). Deliberately NOT
         the assistant face: it says "this section is selected", not "the
         assistant is speaking". -->
    <template v-else-if="icon === 'frame'">
      <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" stroke-width="1.7" />
      <path d="M7 10.5h10M7 14h6" stroke-width="1.7" />
    </template>
    <template v-else-if="icon === 'icons'">
      <circle cx="12" cy="12" r="2" />
      <path
        d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
      />
    </template>
    <template v-else-if="icon === 'seo'">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
      <path d="M8 11h6M11 8v6" />
    </template>
    <template v-else-if="icon === 'form'">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 9h4M7 13h8M7 17h5" />
    </template>
    <template v-else-if="icon === 'clock'">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </template>
    <template v-else-if="icon === 'chart'">
      <path d="M4 20V10M10 20V4M16 20v-8M21 20H3" />
    </template>
    <template v-else-if="icon === 'invoice'">
      <path d="M6 2.5h12v19l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6M9 12h6" />
    </template>
    <template v-else-if="icon === 'support'">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.8 2.1c-.8.5-1.3 1-1.3 2" />
      <path d="M12 17h.01" />
    </template>
    <template v-else-if="icon === 'lock'">
      <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </template>
    <template v-else-if="icon === 'user'">
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c.9-3.3 3.5-5 7-5s6.1 1.7 7 5" />
    </template>
    <template v-else-if="icon === 'business'">
      <path d="M4 21V7l8-4 8 4v14" />
      <path d="M9 21v-5h6v5M8 10h.01M12 10h.01M16 10h.01" />
    </template>
    <template v-else-if="icon === 'files'">
      <path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z" />
      <path d="M13 3v6h6" />
    </template>
    <template v-else-if="icon === 'database'">
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
      <path d="M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3" />
    </template>
    <template v-else-if="icon === 'table'">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M9 4v16" />
    </template>
    <template v-else-if="icon === 'palette'">
      <circle cx="12" cy="12" r="9" />
      <circle cx="9" cy="10" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="14" cy="8" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="16" cy="13" r="1.5" fill="currentColor" stroke="none" />
      <path d="M12 21c-2 0-4-1.5-4-4 0-1.5 1-2.5 2-2.5h4c1 0 2 1 2 2.5 0 2.5-2 4-4 4z" />
    </template>
  </svg>
</template>
